import { splitPhone, normalizeValue } from "./utils";

export const buildStaticPayload = (values, entityId, parentDimensionId) => {
  const clientPhone = splitPhone(values.customerPhone);

  const pointOfContacts =
    (values.pointOfContacts || [])
      .map((p) => {
        const firstName = normalizeValue(p?.firstName);
        const lastName = normalizeValue(p?.lastName);
        const workEmail = normalizeValue(p?.workEmail);
        const isPrimary = Boolean(p?.isPrimary);

        const phoneObj = splitPhone(p?.phone);

        return {
          name: {
            firstName,
            lastName,
          },
          workEmail,
          phoneNumber: {
            countryCode: phoneObj.countryCode,
            number: phoneObj.phoneNumber,
          },
          primary: isPrimary,
        };
      })
      .filter((poc) => {
        return (
          poc.name?.firstName ||
          poc.name?.lastName ||
          poc.workEmail ||
          poc.phoneNumber?.countryCode ||
          poc.phoneNumber?.number
        );
      }) || [];

  if (!entityId || typeof entityId !== "string") {
    throw new Error("Entity ID is required and must be a valid string");
  }

  const payload = {
    dimensionType: "client",
    entityId: entityId,
    parentDimensionId: parentDimensionId || null,

    legalName: normalizeValue(values.customerName),
    clientId: normalizeValue(values.customerId),
    clientEmail: normalizeValue(values.customerEmail),

    clientPhone: {
      countryCode: clientPhone.countryCode,
      number: clientPhone.phoneNumber,
    },

    clientAddress: {
      address: normalizeValue(values.Address1),
      secondAddress: normalizeValue(values.Address2),
      state: normalizeValue(values.state),
      city: normalizeValue(values.city),
      country: normalizeValue(values.country),
      postalCode: normalizeValue(values.postalCode),
    },

    pointOfContacts,

    clientWebsite: normalizeValue(values.customerWebsite),
    tradeLicenseId:
      values.tradeLicenseId !== null &&
      values.tradeLicenseId !== undefined &&
      values.tradeLicenseId !== "" &&
      String(values.tradeLicenseId).trim() !== ""
        ? String(values.tradeLicenseId).trim()
        : null,
  };

  return payload;
};

export const buildDynamicFormPayload = (values, dynamicFormTemplate) => {
  const formTemplateId =
    values?.formTemplateId ||
    (dynamicFormTemplate &&
    Array.isArray(dynamicFormTemplate) &&
    dynamicFormTemplate.length > 0
      ? dynamicFormTemplate[0]?._id
      : null);

  if (!formTemplateId) {
    return {};
  }

  const template =
    dynamicFormTemplate &&
    Array.isArray(dynamicFormTemplate) &&
    dynamicFormTemplate.length > 0
      ? dynamicFormTemplate[0]
      : null;

  if (!template?.questions || !Array.isArray(template.questions)) {
    return {};
  }

  const questions = template.questions
    .map((templateQuestion, idx) => {
      let answerValue = null;

      if (Array.isArray(values?.formQuestions) && values.formQuestions[idx]) {
        answerValue = values.formQuestions[idx]?.answer;
      }

      if (answerValue === null || answerValue === undefined) {
        answerValue = values?.[`formQuestions[${idx}].answer`];
      }

      const answer = { file: null, text: null, date: null, choices: null };

      const originalAnswerType = templateQuestion.answerType;
      let mappedAnswerType = originalAnswerType;

      if (originalAnswerType === "simpleText") {
        mappedAnswerType = "text";
      } else if (
        originalAnswerType === "dropDown" ||
        originalAnswerType === "radio"
      ) {
        mappedAnswerType = "multiple-choice";
      } else if (originalAnswerType === "checkbox") {
        mappedAnswerType = "multiple-choice";
      } else if (
        originalAnswerType === "country" ||
        originalAnswerType === "phone"
      ) {
        mappedAnswerType = "text";
      } else if (originalAnswerType === "number") {
        mappedAnswerType = "text";
      }

      if (
        mappedAnswerType === "text" ||
        originalAnswerType === "simpleText" ||
        originalAnswerType === "country" ||
        originalAnswerType === "number"
      ) {
        if (
          answerValue !== null &&
          answerValue !== undefined &&
          answerValue !== ""
        ) {
          answer.text = String(answerValue);
        }
      } else if (originalAnswerType === "phone") {
        if (
          answerValue !== null &&
          answerValue !== undefined &&
          answerValue !== ""
        ) {
          answer.text = String(answerValue);
        }
      } else if (mappedAnswerType === "date" || originalAnswerType === "date") {
        if (
          answerValue !== null &&
          answerValue !== undefined &&
          answerValue !== "" &&
          !(Array.isArray(answerValue) && answerValue.length === 0)
        ) {
          answer.date = answerValue;
        } else {
          answer.date = null;
        }
      } else if (mappedAnswerType === "file" || originalAnswerType === "file") {
        if (Array.isArray(answerValue) && answerValue.length > 0) {
          const fileItem = answerValue[0];

          if (fileItem?.url && fileItem?.publicId) {
            answer.file = {
              url: fileItem.url,
              publicId: fileItem.publicId,
            };
          } else if (fileItem?.url || fileItem?.publicId) {
            answer.file = {
              url: fileItem.url || null,
              publicId: fileItem.publicId || null,
            };
          } else if (fileItem?.originFileObj) {
            answer.file = null;
          } else {
            answer.file = null;
          }
        } else if (
          answerValue &&
          typeof answerValue === "object" &&
          !Array.isArray(answerValue)
        ) {
          if (answerValue.url && answerValue.publicId) {
            answer.file = {
              url: answerValue.url,
              publicId: answerValue.publicId,
            };
          } else if (answerValue.url || answerValue.publicId) {
            answer.file = {
              url: answerValue.url || null,
              publicId: answerValue.publicId || null,
            };
          } else {
            answer.file = null;
          }
        } else {
          answer.file = null;
        }
      } else if (
        mappedAnswerType === "multiple-choice" ||
        originalAnswerType === "multiple-choice" ||
        originalAnswerType === "dropDown" ||
        originalAnswerType === "radio" ||
        originalAnswerType === "checkbox"
      ) {
        // Get the multiOptions from the template question
        const multiOptions = templateQuestion?.multiOptions || [];

        // Helper function to convert _id to text
        const convertIdToText = (idValue) => {
          if (!idValue) return null;

          // Find the option with matching _id
          const option = multiOptions.find(
            (opt) => opt._id === idValue || String(opt._id) === String(idValue)
          );

          // Return the text if found, otherwise return the original value
          return option?.text || idValue;
        };

        if (Array.isArray(answerValue) && answerValue.length > 0) {
          // Convert each _id to text
          const convertedChoices = answerValue
            .map((idValue) => convertIdToText(idValue))
            .filter((text) => text !== null && text !== undefined);
          // Only set choices if we have valid values, otherwise set to null
          answer.choices =
            convertedChoices.length > 0 ? convertedChoices : null;
        } else if (
          answerValue !== null &&
          answerValue !== undefined &&
          answerValue !== ""
        ) {
          // Convert single _id to text
          const textValue = convertIdToText(answerValue);
          if (textValue !== null && textValue !== undefined) {
            answer.choices = [textValue];
          } else {
            answer.choices = null;
          }
        } else {
          answer.choices = null;
        }
      }

      const questionPayload = {
        questionId: templateQuestion._id,
        answerType: mappedAnswerType,
        answer,
      };

      return questionPayload;
    })
    .filter((q) => q.questionId);

  if (questions.length === 0) {
    return {};
  }

  const formDataPayload = {
    formData: {
      formTemplateId: formTemplateId,
      questions: questions,
    },
  };

  return formDataPayload;
};
